import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Layout,
  Modal,
  Input,
  Select,
  DatePicker,
} from "antd";
import { PlusOutlined, EditOutlined, LockOutlined } from "@ant-design/icons";
import {
  createUser,
  getAgentWithRoles,
  updateUser,
} from "../api/users/userRequest";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { getRolesNames } from "../api/roles/roleRequest";
import PermissionRender from "../components/PermissionRender";

const { Content } = Layout;
const { Option } = Select;

function Users() {
  const [users, setUsers] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    id_agent: null,
    full_name: "",
    full_name_ar: "",
    email: "",
    password: "", // Only shown when creating a user
    assigned_date: "",
    number: "",
    role_ids: [],
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);

  const user = useSelector((state) => state.user.user);
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAgentWithRoles();
      setUsers(
        data.map((it) => ({
          ...it,
          role_ids: it.roles.map((role) => role.id),
        }))
      ); // Update state with the fetched data
    } catch (error) {
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const data = await getRolesNames();
      setRoles(data); // Update state with the fetched data
    } catch (error) {
      message.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  // Handle adding or editing a user
  const handleAddUser = async () => {
    if (
      currentUser.full_name.trim() &&
      currentUser.email.trim() &&
      currentUser.assigned_date.trim() &&
      currentUser.number
    ) {
      setIsSubmitting(true);
      try {
        if (isEditMode) {
          const { password, ...userToUpdate } = currentUser;
          // Only include the password if it's not empty
          if (password.trim()) {
            userToUpdate.password = password;
          }
          await updateUser(userToUpdate); // Update user via API
          console.log(userToUpdate);
          message.success("Utilisateur modifié avec succès");
        } else {
          await createUser(currentUser); // Create new user via API
          message.success("Utilisateur ajouté avec succès");
        }
        resetForm();
        fetchUsers(); // Refresh the user list
      } catch (error) {
        message.error("Error while saving the user");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      message.error(
        // "Veuillez entrer un nom, un email, un mot de passe et un rôle valides"
        " Veuillez remplir tous les champs obligatoires."
      );
    }
  };

  const handleEditUser = (user) => {
    setCurrentUser({ ...user, password: "" }); // Hide password in editing
    setIsEditMode(true);
    setIsModalVisible(true);
  };

  const resetForm = () => {
    setCurrentUser({
      id_agent: null,
      full_name: "",
      full_name_ar: "",
      email: "",
      password: "", // Reset password for new user
      assigned_date: "",
      number: "",
      role_ids: [],
    });
    setIsEditMode(false);
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: "Nom",
      dataIndex: "full_name",
      key: "full_name",
    },
    {
      title: "الاسم",
      dataIndex: "full_name_ar",
      key: "full_name_ar",
      align: "right",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "roles",
      key: "roles",
      render: (roles) => (
        <span>{roles.map((role) => role.name).join(", ")}</span>
      ),
    },
    {
      title: "Date d'affectation",
      dataIndex: "assigned_date",
      key: "assigned_date",
    },
    {
      title: "Référence",
      dataIndex: "number",
      key: "number",
    },

    {
      title: "Action",
      key: "action",
      align: "right",
      render: (text, record) => (
        <Space size="middle">
          <PermissionRender requiredPermission={["users", "update"]}>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            >
              Modifier
            </Button>
          </PermissionRender>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={24}>
              <PermissionRender requiredPermission={["users", "create"]}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    resetForm();
                    setIsModalVisible(true);
                  }}
                  className="w-full"
                >
                  Créer un nouvel utilisateur
                </Button>
              </PermissionRender>
            </Col>
          </Row>
        </Card>

        <Card className="shadow-sm">
          <Table
            dataSource={users.map((user) => ({ key: user.id_agent, ...user }))}
            columns={columns}
            pagination={false}
            loading={loading}
          />
        </Card>

        <Modal
          title={
            isEditMode ? "Modifier l'Utilisateur" : "Ajouter un Utilisateur"
          }
          visible={isModalVisible}
          onOk={handleAddUser}
          onCancel={resetForm}
          confirmLoading={isSubmitting}
        >
          <Input
            placeholder="Nom complet *"
            value={currentUser.full_name}
            onChange={(e) =>
              setCurrentUser({ ...currentUser, full_name: e.target.value })
            }
          />
          <Input
            placeholder="الاسم الكامل"
            value={currentUser.full_name_ar}
            onChange={(e) =>
              setCurrentUser({ ...currentUser, full_name_ar: e.target.value })
            }
            style={{ marginTop: "10px", textAlign: "right", direction: "rtl" }}
          />
          <Input
            placeholder="Email * "
            type="email"
            value={currentUser.email}
            onChange={(e) =>
              setCurrentUser({ ...currentUser, email: e.target.value })
            }
            style={{ marginTop: "10px" }}
          />

          <DatePicker
            format="DD/MM/YYYY"
            placeholder="Date d'affectation * "
            value={
              currentUser.assigned_date
                ? dayjs(currentUser.assigned_date)
                : null
            }
            onChange={(date) =>
              setCurrentUser({
                ...currentUser,
                assigned_date: date?.format("YYYY-MM-DD"),
              })
            }
            className="w-full mt-3"
          />

          <Input
            placeholder="Référence *"
            value={currentUser.number}
            onChange={(e) =>
              setCurrentUser({ ...currentUser, number: e.target.value })
            }
            style={{ marginTop: "10px" }}
          />
          {/* Select input for Role */}
          <Select
            mode="multiple"
            placeholder="Sélectionner des rôles"
            value={currentUser.role_ids || undefined} // Ensure value is undefined if role_ids is empty
            onChange={(value) => {
              setCurrentUser({ ...currentUser, role_ids: value });
            }}
            style={{ width: "100%", marginTop: "10px" }}
            optionLabelProp="label"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {roles.map((role) => (
              <Option key={role.id} value={role.id} label={role.name}>
                <div className="flex items-center">
                  <LockOutlined className="mr-2 text-blue-500" />
                  {role.name}
                </div>
              </Option>
            ))}
          </Select>

          {/* Show password field only in create mode */}
          <Input.Password
            placeholder="Mot de passe *"
            value={currentUser.password}
            onChange={(e) =>
              setCurrentUser({ ...currentUser, password: e.target.value })
            }
            style={{ marginTop: "10px" }}
          />
        </Modal>
      </Content>
    </Layout>
  );
}

export default Users;